/**
 * Lógica pura del centro de notificaciones (ADR 0022 — campanita, fase 4).
 *
 * TODO lo que se puede testear sin DOM vive acá — el hook `useNotificationCenter`
 * y el componente `NotificationBell` son cableado fino sobre estas funciones.
 * Ningún import de React ni de `sonner`: este módulo es puro dato→dato.
 */
import type {
  ConnectionPhase,
  NotificationApiRow,
  NotificationDTO,
  NotificationPushPayload,
  ServerStreamFrame,
} from "./types";

// ---------------------------------------------------------------------------
// Normalización — fila REST / push WS → NotificationDTO
// ---------------------------------------------------------------------------

export function toNotificationDTO(row: NotificationApiRow): NotificationDTO {
  return {
    id: row.id,
    type: row.type,
    title: row.title,
    body: row.body,
    entityType: row.entityType,
    entityId: row.entityId,
    payload: row.payload,
    createdAt: row.createdAt,
    readAt: row.readAt,
    hiddenAt: row.hiddenAt,
  };
}

/**
 * Una notificación recién empujada por WS siempre es nueva: `readAt` y
 * `hiddenAt` no viajan en el push (ver docstring de `NotificationPushPayload`
 * en `types.ts`) porque no pueden ser otra cosa que `null` en el instante en
 * que se crea.
 */
export function fromPushPayload(p: NotificationPushPayload): NotificationDTO {
  return {
    id: p.id,
    type: p.type,
    title: p.title,
    body: p.body,
    entityType: p.entityType,
    entityId: p.entityId,
    payload: p.payload,
    createdAt: p.createdAt,
    readAt: null,
    hiddenAt: null,
  };
}

// ---------------------------------------------------------------------------
// Parsing defensivo del frame del WS — el payload es texto no confiable
// ---------------------------------------------------------------------------

/**
 * Valida y normaliza un mensaje crudo del WebSocket contra el sub-protocolo
 * de ADR 0022 §2. `null` si no matchea ninguna forma conocida — el caller
 * debe ignorarlo silenciosamente (nunca tirar: el DO es transporte, no
 * fuente de verdad, y un frame malformado no debe tumbar la UI).
 *
 * Los campos opcionales del push (`body`/`entityType`/`entityId`/`payload`)
 * se normalizan a su default (`null` / `{}`) en vez de dejarlos ausentes —
 * evita el escollo de `exactOptionalPropertyTypes` (asignar `undefined`
 * explícito a una key opcional es un error de tipos en este repo).
 */
export function parseServerStreamFrame(raw: unknown): ServerStreamFrame | null {
  if (typeof raw !== "object" || raw === null) return null;
  const obj = raw as Record<string, unknown>;

  if (obj.type === "heartbeat") return { type: "heartbeat" };
  if (obj.type === "session_revoked") return { type: "session_revoked" };

  if (obj.type === "notification") {
    const rawPayload = obj.payload;
    if (typeof rawPayload !== "object" || rawPayload === null) return null;
    const row = rawPayload as Record<string, unknown>;
    const { id, type, title, createdAt } = row;
    if (
      typeof id !== "string" ||
      typeof type !== "string" ||
      typeof title !== "string" ||
      typeof createdAt !== "string"
    ) {
      return null;
    }
    return {
      type: "notification",
      payload: {
        id,
        type,
        title,
        body: typeof row.body === "string" ? row.body : null,
        entityType: typeof row.entityType === "string" ? row.entityType : null,
        entityId: typeof row.entityId === "string" ? row.entityId : null,
        payload:
          typeof row.payload === "object" && row.payload !== null
            ? (row.payload as Record<string, unknown>)
            : {},
        createdAt,
      },
    };
  }

  return null;
}

// ---------------------------------------------------------------------------
// Merge / dedupe — ADR 0022 §3: "el cliente deduplica por id"
// ---------------------------------------------------------------------------

/** Tope defensivo de la lista en memoria — evita crecimiento sin límite en una sesión larga. */
const DEFAULT_MERGE_LIMIT = 200;

function compareByCreatedAtDesc(a: NotificationDTO, b: NotificationDTO): number {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

/**
 * Combina la lista actual con notificaciones entrantes (de un catch-up REST
 * o de un único push WS), deduplicando por `id` — si el `id` ya está en
 * `current`, la entrante se descarta (gana la que ya estaba, sin importar el
 * origen). Reordena siempre por `createdAt` desc: ni el catch-up ni el push
 * garantizan llegar en orden.
 */
export function mergeNotificationLists(
  current: NotificationDTO[],
  incoming: NotificationDTO[],
  limit: number = DEFAULT_MERGE_LIMIT,
): NotificationDTO[] {
  const byId = new Map<string, NotificationDTO>();
  for (const n of current) byId.set(n.id, n);
  for (const n of incoming) if (!byId.has(n.id)) byId.set(n.id, n);
  return [...byId.values()].sort(compareByCreatedAtDesc).slice(0, limit);
}

/** Aplica un frame del stream sobre la lista actual. `sessionRevoked` avisa al caller que corte el socket y navegue a login. */
export interface ReduceStreamFrameResult {
  list: NotificationDTO[];
  sessionRevoked: boolean;
}

export function reduceStreamFrame(
  list: NotificationDTO[],
  frame: ServerStreamFrame,
): ReduceStreamFrameResult {
  if (frame.type === "notification") {
    return {
      list: mergeNotificationLists(list, [fromPushPayload(frame.payload)]),
      sessionRevoked: false,
    };
  }
  if (frame.type === "session_revoked") {
    return { list, sessionRevoked: true };
  }
  // heartbeat: no-op, solo keep-alive de intermediarios (ADR 0022 §2).
  return { list, sessionRevoked: false };
}

// ---------------------------------------------------------------------------
// Selectores — contador y lista visible
// ---------------------------------------------------------------------------

/** No leídas Y visibles — una notificación oculta nunca suma al contador aunque no se haya leído. */
export function countUnread(list: NotificationDTO[]): number {
  return list.filter((n) => n.readAt === null && n.hiddenAt === null).length;
}

/** Lo que el panel debe listar — excluye las ocultadas localmente (soft-delete optimista). */
export function visibleNotifications(list: NotificationDTO[]): NotificationDTO[] {
  return list.filter((n) => n.hiddenAt === null);
}

/**
 * Complemento de `visibleNotifications` — lo que la vista "Ocultas" del
 * toggle debe listar. Un push de WS SIEMPRE llega con `hiddenAt: null`
 * (`fromPushPayload`), así que nunca puede colarse acá: el filtro por
 * `hiddenAt` alcanza para que un push en vivo no ensucie la vista de ocultas
 * mientras el usuario la está mirando, sin lógica extra en el reducer del
 * stream (ver docstring de `useNotificationCenter` §Modo "Ocultas").
 */
export function hiddenNotifications(list: NotificationDTO[]): NotificationDTO[] {
  return list.filter((n) => n.hiddenAt !== null);
}

// ---------------------------------------------------------------------------
// Catch-up tras reconexión — ADR 0022 §4
// ---------------------------------------------------------------------------

/**
 * `id` de la notificación más nueva conocida — insumo de
 * `GET /api/notifications?since=<id>` al reconectar el WS. `undefined` con
 * lista vacía (primera carga: sin cursor, se trae todo).
 */
export function computeSinceId(list: NotificationDTO[]): string | undefined {
  let newest: NotificationDTO | undefined;
  for (const n of list) {
    if (!newest || new Date(n.createdAt).getTime() > new Date(newest.createdAt).getTime()) {
      newest = n;
    }
  }
  return newest?.id;
}

// ---------------------------------------------------------------------------
// Mutaciones locales optimistas (read/hide/unhide)
// ---------------------------------------------------------------------------

export function markLocalRead(
  list: NotificationDTO[],
  id: string,
  atISO: string,
): NotificationDTO[] {
  return list.map((n) => (n.id === id && n.readAt === null ? { ...n, readAt: atISO } : n));
}

export function markLocalHidden(
  list: NotificationDTO[],
  id: string,
  atISO: string,
): NotificationDTO[] {
  return list.map((n) => (n.id === id ? { ...n, hiddenAt: atISO } : n));
}

export function markLocalUnhidden(list: NotificationDTO[], id: string): NotificationDTO[] {
  return list.map((n) => (n.id === id ? { ...n, hiddenAt: null } : n));
}

// ---------------------------------------------------------------------------
// Reconexión — backoff exponencial (ADR 0022 §4: 1s, 2s, 5s, 10s, tope 30s)
// ---------------------------------------------------------------------------

const BACKOFF_SEQUENCE_MS = [1000, 2000, 5000, 10000, 30000] as const;

/** `attempt` es 0-based (primer reintento = 0). Cualquier valor ≥ el largo de la secuencia cae al tope. */
export function computeBackoffDelayMs(attempt: number): number {
  const index = Math.min(Math.max(attempt, 0), BACKOFF_SEQUENCE_MS.length - 1);
  return BACKOFF_SEQUENCE_MS[index] ?? 30000;
}

// ---------------------------------------------------------------------------
// URL del stream — http(s) → ws(s)
// ---------------------------------------------------------------------------

export function buildStreamUrl(apiBaseUrl: string): string {
  const url = new URL("/api/notifications/stream", apiBaseUrl);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

// ---------------------------------------------------------------------------
// Navegación al hacer click — entityType/entityId (ADR 0022 §5, polimórfico)
// ---------------------------------------------------------------------------

/**
 * Resuelve a qué ruta interna navega el click de una notificación con
 * entidad asociada. `null` si no hay entidad o el tipo no tiene destino
 * conocido — el click igual marca como vista, solo no navega.
 *
 * Único `entityType` mapeado hoy: `"dashboard"` → `/dashboard/:id` (ruta ya
 * existente en `main.tsx` para un dashboard publicado del tenant). NINGÚN
 * productor real emite `entityType`/`entityId` todavía (el único productor
 * mergeado, `email-failure-inbox.ts`, los manda `null`) — este mapeo queda
 * como groundwork hacia adelante para cuando un productor tipo
 * "dashboard.published" los popule (ADR 0022 §9 lo deja explícitamente
 * pospuesto). Extender este `switch` es el único cambio necesario cuando
 * aparezca un productor nuevo.
 */
export function resolveNotificationTarget(
  n: Pick<NotificationDTO, "entityType" | "entityId">,
): string | null {
  if (!n.entityType || !n.entityId) return null;
  switch (n.entityType) {
    case "dashboard":
      return `/dashboard/${encodeURIComponent(n.entityId)}`;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Tiempo relativo — timestamps de la lista y del indicador de conexión
// ---------------------------------------------------------------------------

export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffSec = Math.max(0, Math.round((now.getTime() - then) / 1000));
  if (diffSec < 10) return "recién";
  if (diffSec < 60) return `hace ${diffSec}s`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `hace ${diffMin} min`;
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 24) return `hace ${diffHour} h`;
  const diffDay = Math.round(diffHour / 24);
  return `hace ${diffDay} d`;
}

/**
 * Texto discreto del estado de conexión (ADR 0022 §8: la caída tiene que
 * verse sin bloquear). `"open"` → "En vivo"; cualquier otra fase cae al
 * último momento sincronizado, o a un estado neutro si todavía no hubo
 * ningún fetch.
 */
export function describeConnectionStatus(
  phase: ConnectionPhase,
  lastSyncAt: Date | null,
  now: Date = new Date(),
): string {
  if (phase === "open") return "En vivo";
  if (!lastSyncAt) return phase === "connecting" ? "Conectando…" : "Sin conexión en vivo";
  return `Actualizado ${formatRelativeTime(lastSyncAt.toISOString(), now)}`;
}
